import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError, onSessionExpired } from '@/lib/api/client';
import { routeTree } from './routeTree.gen';
import './styles/app.css';

/**
 * Query defaults tuned for a POS back-office on a store LAN:
 * data is cheap to refetch and staleness is expensive, so we keep windows
 * short and refetch on focus — an operator tabbing back expects live numbers.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Never retry an auth or business-rule refusal — it will just fail
        // again and delay the error the operator needs to see.
        if (
          error instanceof ApiError &&
          (error.isAuth || (error.status >= 400 && error.status < 500))
        ) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
    },
    mutations: {
      // Writes are never retried automatically. Most of this API is not
      // idempotent (see AGENTS.md money rules) — a silent replay could
      // double-charge. Retrying is always an explicit operator action.
      retry: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// A failed refresh means the session is truly gone. Bounce to /login and
// remember where the operator was so they land back there.
onSessionExpired(() => {
  const here = window.location.pathname + window.location.search;
  if (window.location.pathname !== '/login') {
    queryClient.clear();
    void router.navigate({
      to: '/login',
      search: { redirect: here },
      replace: true,
    });
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);
